import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DvrOutlinedIcon from '@mui/icons-material/DvrOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { enqueueSnackbar } from "notistack";
import React, { useContext } from "react";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { AuthContext } from "../../../contexts/AuthContext";
import { getGroupEmail } from "../../../helpers/authUser";
import theme from "../../../theme";

const useStyles = makeStyles((theme) => ({
    container: {
        width: 350,
        height: 180,
        border: `2px solid ${theme.colors.neutral['200']}`,
        borderRadius: 24,
        padding: 16,
    },
    headerIcon: {
        color: theme.colors.neutral['400'],
        width: 60,
        height: 60,
    },
    fullPageContainer: {
        width: '100%',
        height: '100%',
        padding: theme.spacing(2),
    },
    fullPageIconWrapper: {
        width: 88,
        height: 88,
        borderRadius: 18,
        backgroundColor: 'rgb(233, 250, 248)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    fullPageIcon: {
        color: theme.colors.cyan['700'],
        width: 44,
        height: 44,
    },
    fullPageTitle: {
        fontFamily: 'Inter',
        fontSize: '1.3rem',
        fontWeight: 700,
        color: theme.colors.neutral['800'],
        textAlign: 'center',
    },
    fullPageSubtitle: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 400,
        color: '#666666',
        textAlign: 'center',
        maxWidth: 500,
        lineHeight: 1.5,
    },
    emailBox: {
        border: `1px solid ${theme.colors.cyan['200']}`,
        borderRadius: 12,
        padding: '8px 10px 8px 16px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'rgb(233, 250, 248)',
        minWidth: 400,
        maxWidth: '100%',
    },
    emailLink: {
        flex: 1,
        minWidth: 0,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: theme.colors.neutral['700'],
        cursor: 'pointer',
    },
    copyButton: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 700,
        color: theme.colors.cyan['600'],
        textTransform: 'none',
        border: `1px solid ${theme.colors.cyan['200']}`,
        borderRadius: 10,
        backgroundColor: theme.colors.shades['0'],
        padding: '4px 18px',
        minWidth: 'unset',
        '&:hover': {
            backgroundColor: theme.colors.shades['0'],
        },
    },
    subText: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 400,
        color: theme.colors.neutral['500'],
    },
    link: {
        maxWidth: '24ch',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.colors.cyan['500'],
        cursor: 'pointer',
    },
    break: {
    },
}));

const ForwardDealsCard: React.FC<{ fullPage?: boolean }> = ({ fullPage }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);

    if (fullPage) {
        return (
            <Stack className={classes.fullPageContainer} alignItems="center" justifyContent="center" spacing={5}>
                <Stack alignItems="center" spacing={3}>
                    <Box className={classes.fullPageIconWrapper}>
                        <DvrOutlinedIcon className={classes.fullPageIcon} />
                    </Box>
                    <Typography className={classes.fullPageTitle}>{'Start forwarding'}</Typography>
                </Stack>
                <Typography className={classes.fullPageSubtitle}>
                    <span>{'Forward any emails containing data on companies'}</span>
                    <span style={{ display: 'block', marginTop: 4 }}>{"that you'd like to log."}</span>
                    <span style={{ display: 'block', marginTop: 4 }}>{"We'll handle the rest."}</span>
                </Typography>
                <Box className={classes.emailBox} mt={1}>
                    <MailOutlineIcon sx={{ color: theme.colors.cyan['600'], width: 22, height: 22, flexShrink: 0 }} />
                    <Typography className={classes.emailLink}
                        onClick={() => window.open(`mailto: ${getGroupEmail(userGroup)}`, '_self', 'noopener, noreferrer')}>
                        {getGroupEmail(userGroup)}
                    </Typography>
                    <CopyToClipboard text={`${getGroupEmail(userGroup)}`}
                        options={{ format: "text/plain" }}
                        onCopy={() => {
                            enqueueSnackbar(`Successfully copied to clipboard.`,
                                { anchorOrigin: { vertical: "bottom", horizontal: "right" }, });
                        }}>
                        <Button className={classes.copyButton} disableRipple>{'Copy'}</Button>
                    </CopyToClipboard>
                </Box>
            </Stack>
        );
    }

    return (<>
        <Stack className={classes.container} spacing={1} alignItems="center" justifyContent="center">
            <ForwardToInboxOutlinedIcon className={classes.headerIcon} />
            <Typography className={classes.subText}>
                {'Forward emails with deals to: '}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Typography className={classes.link}
                    onClick={() => window.open(`mailto: ${getGroupEmail(userGroup)}`, '_self', 'noopener, noreferrer')}>
                    {getGroupEmail(userGroup)}
                </Typography>
                <CopyToClipboard text={`${getGroupEmail(userGroup)}`}
                    options={{ format: "text/plain" }}
                    onCopy={() => {
                        enqueueSnackbar(`Successfully copied to clipboard.`,
                            { anchorOrigin: { vertical: "bottom", horizontal: "right" }, });
                    }}>
                    <IconButton size="small" sx={{ color: theme.colors.cyan['500'] }}>
                        <ContentCopyIcon sx={{ width: 24, height: 24, }} />
                    </IconButton>
                </CopyToClipboard>
            </Stack>
        </Stack>
    </>);
}

export default ForwardDealsCard;
